// The game-flow state stack: the game names its states (boot, title, loading, play, pause, game over, credits…) and
// their hooks; the stack pushes, pops and replaces them. A state pushed over another (pause over play) covers it
// without exiting it, so play keeps its state underneath. Transitions requested from inside a hook are queued and
// run after the current one, so hooks can chain (boot's exit can't be interrupted by title's enter).
// Genre-neutral: no state names live here; the game passes its table.

/** A state's hooks. All optional. */
export interface FlowStateDef<Id extends string> {
  /** The state became part of the stack (pushed or replaced in). `data` is what the transition passed. */
  enter?(data: unknown, flow: Flow<Id>): void;
  /** The state left the stack (popped or replaced out). */
  exit?(flow: Flow<Id>): void;
  /** Another state was pushed over this one. */
  cover?(by: Id, flow: Flow<Id>): void;
  /** The state over this one was popped: this one is on top again. */
  uncover?(from: Id, flow: Flow<Id>): void;
}

/** One transition, as the history and listeners see it. */
export interface FlowEvent<Id extends string> {
  op: "push" | "pop" | "replace" | "reset";
  /** The top before. */
  from: Id | null;
  /** The top after. */
  to: Id | null;
  /** The whole stack after, bottom first. */
  stack: Id[];
  /** Sequence number (0, 1, 2…). */
  seq: number;
}

/** The flow's options: the game's states and, optionally, which transitions are legal. */
export interface FlowOptions<Id extends string> {
  states: Record<Id, FlowStateDef<Id>>;
  /**
   * Legal targets of push and replace, keyed by the current top (`""` for an empty stack). A top with no entry may go
   * anywhere; omit the table to allow everything. An illegal transition throws.
   */
  allowed?: Partial<Record<Id | "", readonly Id[]>>;
  /** Cap on the kept history. Default 200. */
  historyCap?: number;
}

/** The flow state stack. Build one with `createFlow`. */
export class Flow<Id extends string> {
  private readonly items: Id[] = [];
  private readonly listeners = new Set<(e: FlowEvent<Id>) => void>();
  private readonly queue: (() => void)[] = [];
  private busy = false;
  private seq = 0;
  /** Every transition so far (capped), oldest first. */
  readonly history: FlowEvent<Id>[] = [];

  constructor(private readonly o: FlowOptions<Id>) {}

  /** The state on top, or null when the stack is empty. */
  get top(): Id | null {
    return this.items[this.items.length - 1] ?? null;
  }

  /** The stack, bottom first (a copy). */
  get stack(): Id[] {
    return [...this.items];
  }

  /** Whether `id` is on top. */
  is(id: Id): boolean {
    return this.top === id;
  }

  /** Whether `id` is anywhere in the stack. */
  has(id: Id): boolean {
    return this.items.includes(id);
  }

  /** Listen to transitions; returns the remover. */
  on(fn: (e: FlowEvent<Id>) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Push `id` over the current top (which is covered, not exited). */
  push(id: Id, data?: unknown): void {
    this.run(() => {
      this.check(id);
      const from = this.top;
      if (from !== null) this.def(from).cover?.(id, this);
      this.items.push(id);
      this.def(id).enter?.(data, this);
      this.emit("push", from);
    });
  }

  /** Pop the top (it exits; the one below is uncovered). Throws on an empty stack. */
  pop(): void {
    this.run(() => {
      const from = this.top;
      if (from === null) throw new Error("flow: pop on an empty stack");
      this.items.pop();
      this.def(from).exit?.(this);
      const top = this.top;
      if (top !== null) this.def(top).uncover?.(from, this);
      this.emit("pop", from);
    });
  }

  /** Replace the top with `id` (the old top exits, the new one enters). On an empty stack, the same as push. */
  replace(id: Id, data?: unknown): void {
    this.run(() => {
      this.check(id);
      const from = this.top;
      if (from !== null) {
        this.items.pop();
        this.def(from).exit?.(this);
      }
      this.items.push(id);
      this.def(id).enter?.(data, this);
      this.emit("replace", from);
    });
  }

  /** Exit every state, top first, then enter `id` alone (back to the title from anywhere). Not checked against `allowed`. */
  reset(id: Id, data?: unknown): void {
    this.run(() => {
      const from = this.top;
      while (this.items.length) this.def(this.items.pop()!).exit?.(this);
      this.items.push(id);
      this.def(id).enter?.(data, this);
      this.emit("reset", from);
    });
  }

  private def(id: Id): FlowStateDef<Id> {
    const d = this.o.states[id];
    if (!d) throw new Error(`flow: unknown state "${id}"`);
    return d;
  }

  private check(to: Id): void {
    this.def(to);
    const allowed = this.o.allowed?.[this.top ?? ""];
    if (allowed && !allowed.includes(to)) throw new Error(`flow: ${this.top ?? "(empty)"} → ${to} is not allowed`);
  }

  private emit(op: FlowEvent<Id>["op"], from: Id | null): void {
    const e: FlowEvent<Id> = { op, from, to: this.top, stack: this.stack, seq: this.seq++ };
    this.history.push(e);
    if (this.history.length > (this.o.historyCap ?? 200)) this.history.shift();
    for (const fn of this.listeners) fn(e);
  }

  // Transitions requested while one runs (from a hook or a listener) wait their turn.
  private run(f: () => void): void {
    if (this.busy) {
      this.queue.push(f);
      return;
    }
    this.busy = true;
    try {
      f();
      while (this.queue.length) this.queue.shift()!();
    } finally {
      this.busy = false;
      this.queue.length = 0;
    }
  }
}

/** Build a flow over the game's state table. The stack starts empty: push or replace the first state. */
export function createFlow<Id extends string>(o: FlowOptions<Id>): Flow<Id> {
  return new Flow(o);
}
